import { registerContextServices } from '@packages/contexts/container';
import type { Container } from '@packages/core/container';
import { getContainer as getCoreContainer } from '@packages/core/container';
import { registerDomainPackage } from '@packages/domain';

import { registerWebPrimitives, registerWebServices } from './register-web-services';
import { registerWebLogging } from './register-web-logging';
import type { WebContainerOptions } from './types';

let cached: Container | null = null;

/**
 * Get the web IoC container, creating it on first call and caching for subsequent calls.
 * Bootstraps all layers (core, contexts, clients, domain, web), seeds platform primitives,
 * and registers web-only services into a single unified container.
 *
 * @param options - container retrieval options
 * @returns the fully-configured container ready for use
 */
export function getContainer(options: WebContainerOptions = {}): Container {
    const { useCache = true, autoRegisterWebPrimitives = true } = options;

    if (useCache && cached) {
        return cached;
    }

    // Build the base container and register all service layers.
    const container = getCoreContainer(options);
    registerContextServices(container);

    if (autoRegisterWebPrimitives) {
        registerWebPrimitives(container);
        registerWebLogging(container);
    }

    registerDomainPackage(container);
    registerWebServices(container);

    cached = container;
    return container;
}
